import { useAppSelector } from "@jidlomat/terminal-core/hooks/utilsHooks/storeHooks";
import { selectInitialDataLoaded } from "@jidlomat/terminal-core/slices/appStateSlice";
import { selectInventoryTracking } from "@jidlomat/terminal-core/slices/fridgeInfoSlice";
import type { TerminalFlow } from "@jidlomat/terminal-core/types/terminalFlow";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { askForReload, isOriginReachable, untilReloadAllowed } from "../recovery/reloadSteps";
import { loadFlow, resolveFlowKind } from "./loadFlow";
import { reportFlowFailure } from "./reportFlowFailure";

export type TerminalFlowState = {
	/** The flow of this fridge, or null until its chunk has arrived. */
	flow: TerminalFlow | null;
	/** The chunk could not be fetched and no reload is on its way to fix it. */
	failed: boolean;
};

// A reload that was asked for takes the page away within moments. Meant to be longer than a slow
// navigation, because the old page lives until the new one answers; where it is not, the error
// screen shows for the moment before the reload.
const RELOAD_GRACE_MS = 10_000;

/** Reports the failure like reportFlowFailure, and never throws. */
const reportWithoutThrowing: typeof reportFlowFailure = (kind, rejection) => {
	try {
		reportFlowFailure(kind, rejection);
	} catch {
		// A report that fails must not end the handling before a next attempt is armed.
	}
};

/**
 * Loads the flow of this fridge once the boot query has settled: with the initial state, because
 * only then is it known what the fridge tracks, or without one, because the page then falls through
 * to the menu and the hourly refetch fills it.
 *
 * The kiosk runs without an HTTP cache, so the chunk is fetched on every boot, and the fetch can
 * fail. A failure reloads the page if the origin answers, rate-limited like every recovery reload.
 * Otherwise the fetch is repeated when the connection comes back or when the rate limit lets go,
 * whichever is first; a second failure can then reload. Not every engine fetches a module again
 * after its import has failed (Chromium keeps the failure): there the repeated fetch fails at once,
 * and the reload it then asks for is what recovers.
 *
 * @param appConnected whether the app is online; its rising edge repeats a failed fetch
 * @param bootSettled whether the queries of the device are at rest
 */
export const useTerminalFlow = (appConnected: boolean, bootSettled: boolean): TerminalFlowState => {
	const initialDataLoaded = useAppSelector(selectInitialDataLoaded);
	// Read live. Today the kind comes from the kiosk's URL before the first render and never changes.
	// Once the backend reports it, a change after the flow is mounted has to become a reload (the flow
	// may have been chosen without a state).
	const kind = resolveFlowKind(useAppSelector(selectInventoryTracking));
	const [state, setState] = useState<TerminalFlowState>({ flow: null, failed: false });
	const [attempt, setAttempt] = useState(0);

	// Latched: the device reports loading again with every hourly refetch.
	const [ready, setReady] = useState(false);
	useEffect(() => {
		if (initialDataLoaded || bootSettled) {
			setReady(true);
		}
	}, [initialDataLoaded, bootSettled]);

	// How often the connection has come back. An attempt compares it with what it started with.
	const reconnectsRef = useRef(0);

	// biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` is what repeats the fetch, it is not read
	useEffect(() => {
		if (!ready) {
			return;
		}
		let disposed = false;
		let retryTimer: number | undefined;
		let graceTimer: number | undefined;
		const reconnectsAtStart = reconnectsRef.current;
		const sayFailed = () => setState({ flow: null, failed: true });
		loadFlow(kind).then(
			(flow) => {
				if (!disposed) {
					setState({ flow: flow, failed: false });
				}
			},
			async (error: unknown) => {
				if (disposed) {
					return;
				}
				reportWithoutThrowing(kind, error);
				const reachable = await isOriginReachable();
				if (disposed) {
					return;
				}
				const reloading = reachable && askForReload("flow chunk failed to load");
				if (!reloading && reconnectsRef.current !== reconnectsAtStart) {
					// The connection came back while this attempt was failing. That edge is over, so it
					// is taken here; the next attempt gets no second one without a new edge.
					setAttempt((current) => current + 1);
					return;
				}
				if (reloading) {
					// A webview can keep its document when the one it navigates to cannot be fetched.
					// Where the page is still here after a while, the reload did not happen. A reconnect
					// in the meantime was not taken, because a reload was on its way: it is taken now.
					// Otherwise the page says that it failed, which lets the next reconnect repeat the
					// fetch.
					graceTimer = window.setTimeout(() => {
						if (reconnectsRef.current === reconnectsAtStart) {
							sayFailed();
						} else {
							setAttempt((current) => current + 1);
						}
					}, RELOAD_GRACE_MS);
				} else {
					sayFailed();
				}
				// Armed in every case, so that nothing leaves the tablet waiting for good.
				retryTimer = window.setTimeout(() => setAttempt((current) => current + 1), untilReloadAllowed());
			},
		);
		return () => {
			disposed = true;
			window.clearTimeout(retryTimer);
			window.clearTimeout(graceTimer);
		};
	}, [ready, kind, attempt]);

	const wasConnectedRef = useRef(appConnected);
	// A layout effect, because it runs within the commit that shows the connection back. A passive
	// effect runs a moment later, and an answer of the origin that arrived in between would find the
	// reconnect not counted yet.
	useLayoutEffect(() => {
		const reconnected = appConnected && !wasConnectedRef.current;
		wasConnectedRef.current = appConnected;
		if (!reconnected) {
			return;
		}
		reconnectsRef.current += 1;
		if (state.failed) {
			setAttempt((current) => current + 1);
		}
	}, [appConnected, state.failed]);

	return state;
};
